import {
  useDynamicContext,
  useUpgradeToDynamicWaasFlow,
} from "@dynamic-labs/sdk-react-core";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { RootState } from "../../redux/store";
import { Paths } from "../../common/constants";
import { useAppSelector } from "../../hooks/useAppSelector";
import { MANUFACTURER_TYPE } from "../../common/constants/enum";

interface Permissions {
  announcements?: { edit: boolean };
  strains?: { edit: boolean };
  rewards_management?: { edit: boolean };
  qr_code?: { edit: boolean };
  batches?: { edit: boolean };
  employees_management?: { edit: boolean };
  api_connection?: { edit: boolean };
  company_profile?: { view?: boolean; edit?: boolean };
  [key: string]: { view?: boolean; edit?: boolean } | undefined;
}

const usePrivateContainer = ({
  moduleKey,
  path,
}: {
  moduleKey: keyof Permissions | undefined;
  path?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const { myProfileData, isFetched } = useAppSelector(
    (state: RootState) => state.myProfile,
  );

  const { user, primaryWallet } = useDynamicContext();

  const { promptUpgradeToDynamicWaasFlow } = useUpgradeToDynamicWaasFlow();

  const { permissions, userType, hasSubscription } = myProfileData;

  const { handleLogOut } = useDynamicContext();

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!user || !primaryWallet) return;

    const runMigration = async () => {
      await new Promise((res) => setTimeout(res, 1500));

      if (primaryWallet.key !== "dynamicwaas") {
        promptUpgradeToDynamicWaasFlow();
      }
    };

    runMigration();

    // eslint-disable-next-line
  }, [user?.userId, primaryWallet?.id]);

  useEffect(() => {
    if (userType) {
      if (permissions) {
        if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_USER ||
          path === Paths.SUPPORT_PAGE
        )
          return;

        if (
          (path === Paths.MY_PROFILE ||
            path === Paths.EDIT_MY_PROFILE ||
            path === Paths.SUPPORT_PAGE ||
            path === Paths.CONSUMERS_MANAGEMENT ||
            path === Paths.CONSUMERS_MANAGEMENT_DETAIL) &&
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE
        )
          return;

        if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          path === Paths.ANALYTICS &&
          !permissions?.analytics?.view
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        }

        if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          path === Paths.EDIT_COMPANY_PROFILE &&
          !permissions?.company_profile?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        }

        if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          path === Paths.ADD_ANNOUNCEMENT &&
          !permissions?.announcements?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          (path === Paths.ADD_STRAIN ||
            path === Paths.EDIT_STRAIN ||
            path === Paths.UPLOAD_STRAINS) &&
          !permissions?.strains?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          path === Paths.ADD_REWARD &&
          !permissions?.rewards_management?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          (path === Paths.PAYMENT_SUCESS ||
            path === Paths.SERIAL_NUMBER ||
            path === Paths.ORDER_DETAIL ||
            path === Paths.CART) &&
          !permissions?.qr_code?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          (path === Paths.UPLOAD_BATCHES ||
            path === Paths.COA_TOKENIZE ||
            path === Paths.PRODUCT_TOKENIZE) &&
          !permissions?.batches?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          (path === Paths.ADD_EMPLOYEES || path === Paths.EDIT_EMPLOYEES) &&
          !permissions?.employees_management?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          (path === Paths.ADD_INTEGRATION || path === Paths.EDIT_INTEGRATION) &&
          !permissions?.api_connection?.edit
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        } else if (
          userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
          (path === Paths.ACCOUNT_LIST ||
            path === Paths.ADD_ACCOUNT ||
            path === Paths.ACCOUNT_DETAILS ||
            path === Paths.MY_SUBSCRIPTION)
        ) {
          navigate(Paths.ACCESS_DENIED);
          return;
        }

        let hasPermission = false;

        for (const key in permissions) {
          if (
            key === moduleKey &&
            permissions[key as keyof Permissions]?.view
          ) {
            hasPermission = true;
            break;
          }
        }

        if (!hasPermission) {
          navigate(Paths.ACCESS_DENIED);
        }
      }

      if (
        userType === MANUFACTURER_TYPE.MANUFACTURER_USER &&
        hasSubscription === false
      ) {
        navigate(Paths.SUBSCRIPTION);
        return;
      } else if (
        userType === MANUFACTURER_TYPE.MANUFACTURER_EMPLOYEE &&
        hasSubscription === false
      ) {
        handleLogOut();
        navigate(Paths.LOGIN);
        localStorage.clear();

        return;
      }
    }
    // eslint-disable-next-line
  }, [permissions, moduleKey, hasSubscription, userType, path]);

  useEffect(() => {
    if (
      location.pathname !== Paths.UPLOAD_BATCHES &&
      location.pathname !== Paths.ADD_STRAIN
    ) {
      localStorage.removeItem("batchAssociate");
      localStorage.removeItem("importedBatchName");
      // localStorage.removeItem("importedBatchId");
      localStorage.removeItem("showUploadBatchListing");
    }
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (
      location.pathname !== Paths.BATCHES &&
      location.pathname !== Paths.ADD_STRAIN
    ) {
      localStorage.removeItem("BatchModalOpen");
      localStorage.removeItem("importedBatchId");
    }
  }, [location.pathname]);

  return {
    isOnline,
    permissions,
    userType,
    isFetched,
  };
};

export default usePrivateContainer;
